import React, { useState } from 'react';
import { MapPin, Phone, Mail, Sparkles, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { trackConversion } from '@/lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SiteFooter = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    const val = email.trim();
    if (!val || !val.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/reminders/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: val }),
      });
      if (!res.ok) throw new Error();
      setSubscribed(true);
      trackConversion('newsletter_signup');
      toast.success("You're in! See you on the 1st of next month 🔮");
    } catch {
      toast.error('Subscription failed, please try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="mt-16 border-t border-[#d4c4a8]/10 pt-10 pb-6" data-testid="site-footer">
      {/* Newsletter signup */}
      <div className="max-w-xl mx-auto px-6 mb-12">
        <div className="rounded-2xl border border-[#e89b47]/20 bg-[#1e2a4a]/40 p-8 text-center" data-testid="footer-newsletter">
          <p className="font-display text-xs tracking-[0.3em] text-[#e89b47] uppercase mb-3">
            Subscribe to the newsletter
          </p>
          <p className="font-body text-sm text-[#d4c4a8]/70 leading-relaxed mb-5 max-w-md mx-auto">
            One email a month — a gentle nudge to draw a fresh reading and realign your strategy. Unsubscribe anytime.
          </p>
          {subscribed ? (
            <div className="inline-flex items-center gap-2 text-[#e89b47] font-body text-sm" data-testid="footer-newsletter-success">
              <CheckCircle className="w-4 h-4" />
              You&rsquo;re subscribed — see you on the 1st!
            </div>
          ) : (
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row items-stretch gap-3 max-w-md mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="flex-1 px-4 py-3 rounded-xl bg-[#0a1628]/60 border border-[#d4c4a8]/20 text-[#e8dcc8] placeholder-[#d4c4a8]/30 font-body text-sm focus:outline-none focus:border-[#e89b47]/60"
                data-testid="footer-newsletter-input"
              />
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-gradient-to-r from-[#e89b47] to-[#d4883a] text-[#0a1628] font-display tracking-widest text-xs uppercase shadow-lg shadow-[#e89b47]/20 disabled:opacity-60 hover:scale-[1.02] transition-transform"
                data-testid="footer-newsletter-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Subscribe
              </button>
            </form>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 mb-8">
        <p className="font-display text-xs tracking-[0.3em] text-[#e89b47] uppercase text-center mb-5">
          Reach us through
        </p>
        <ul className="flex flex-col sm:flex-row items-center justify-center gap-x-8 gap-y-3">
          <li className="flex items-center gap-2 text-[#d4c4a8]/70 font-body text-sm" data-testid="footer-address">
            <MapPin className="w-4 h-4 text-[#e89b47]/70 shrink-0" />
            <span>536 Rosedale Road, Princeton, NJ</span>
          </li>
          <li className="flex items-center gap-2 text-[#d4c4a8]/70 font-body text-sm" data-testid="footer-phone">
            <Phone className="w-4 h-4 text-[#e89b47]/70 shrink-0" />
            <a href="tel:+13472859992" className="hover:text-[#e89b47] transition-colors">+1 (347) 285-9992</a>
          </li>
          <li className="flex items-center gap-2 text-[#d4c4a8]/70 font-body text-sm" data-testid="footer-email">
            <Mail className="w-4 h-4 text-[#e89b47]/70 shrink-0" />
            <a href="mailto:Clarity@themarketingtarot.com" className="hover:text-[#e89b47] transition-colors">Clarity@themarketingtarot.com</a>
          </li>
        </ul>
      </div>
      <div className="flex items-center justify-center gap-2 text-[#d4c4a8]/30 text-sm">
        <span>✦</span>
        <span className="font-body">Strategic insights for marketing visionaries</span>
        <span>✦</span>
      </div>
    </footer>
  );
};

export default SiteFooter;
